<template>
	<div
		v-if="pdf"
		class="pdf-container"
		ref="pdfRef"
	>
		<div class="pdf-meta-list">
			<table v-if="showPdfMeta">
				<tbody>
					<tr>
						<th>Pages:</th>
						<td v-if="pageLimit != null">{{ totalPages }} (Rendering {{ maxPagesToRender }})</td>
						<td v-else>{{ totalPages }}</td>
					</tr>
					<tr v-if="pageSizeInches">
						<th>Size:</th>
						<td>{{ pageSizeInches.width }}" x {{ pageSizeInches.height }}"</td>
					</tr>
				</tbody>
			</table>
		</div>

		<PDFPage
			v-for="(page, index) in pages"
			ref="pdfPages"
			v-bind="{ page }"
			:key="index"
			:index="index"
			:width="pageWidth ?? pdfRef.offsetWidth"
			@rendered="rendered"
			@error="errorRendering"
		>
			<div
				v-show="showFields"
				v-if="page.fields"
				class="pdf-preview-page-fields"
			>
				<PDFPageFieldsOverlay
					v-for="(field, fieldIndex) in page.fields"
					:key="fieldIndex"
					:data="field"
					:onClickField="onClickField"
				/>
			</div>

			<div
				v-for="(overlay, overlayIndex) in page.overlays"
				:key="overlayIndex"
			>
				<PDFSignature
					v-if="showSignature(overlay)"
					:overlay="overlay"
					:pdfRef="pdfRef"
					:style="{
						transform: 'translateZ(0px)',
						position: 'absolute',
						bottom: page.scale * overlay.signature.lowerLeftY + 'px',
						left: page.scale * overlay.signature.lowerLeftX + 'px',
					}"
				/>
				<PDFInitials
					v-else-if="showInitials(overlay)"
					:overlay="overlay"
					:pdfRef="pdfRef"
					:style="{
						transform: 'translateZ(0px)',
						position: 'absolute',
						bottom: page.scale * overlay.initials.lowerLeftY + 'px',
						left: page.scale * overlay.initials.lowerLeftX + 'px',
					}"
				/>
			</div>
		</PDFPage>

		<!-- Triggers next page render. -->
		<div
			v-seen="{ callback: showNextPage }"
			class="pxheight100"
		></div>
	</div>
</template>
<script lang="ts">
	export interface IPDFViewerProps {
		/** Either a base-64 byte string of the PDF or a file URL */
		src?: string;

		/** Provide this if you want to specify your own PDF initialization parameters. If you provide this, `src` and `headers` will be ignored. */
		customDocumentInitParams?: DocumentInitParameters;

		/** A list of overlays to display on top of the PDF */
		overlays?: IDocumentOverlay[];

		/** A list of HTTP headers. Only used if src is a file URL */
		headers?: { [headerKey: string]: string } | string;

		/** The ID of the signer (typically FIMenuCustomer.id) */
		signer?: string;

		/** A list of PDF fields to append to the displayed PDF */
		fields?: PaperworkDocumentPreviewResponse;

		/** Only applicable if displaying PDF fields. An action to perform when a field has been clicked. */
		onClickField?: (field: PDFFieldBox) => void;

		/** Whether or not to display PDF fields. See PDFPageFieldsOverlay.vue. */
		showFields?: boolean;

		/** When true, will display metadata about the PDF file, such as page number and page dimensions, at the top of the component */
		showPdfMeta?: boolean;

		/** When provided, will not render more pages than the number listed. */
		pageLimit?: number;

		pageWidth?: number | string;
	}
</script>
<script setup lang="ts">
	import { computed, inject, onMounted, onBeforeUnmount, markRaw, ref, watch } from 'vue';
	import { PaperworkDocumentPreviewField, PaperworkDocumentPreviewResponse } from '@core/classes/Paperwork/PaperworkDocumentPreviewResponse';
	import { PDFFieldBox, PDFPageData } from '@core/classes/Paperwork/PDFPageData';

	import { DocumentInitParameters, PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';

	import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
	import { IDocumentOverlay } from '@core/classes/Paperwork/DocumentOverlay';
	import { Emitter } from 'mitt';
	import ENUMS from '@core/classes/Enums';
	import IntersectionObserverDirective from '@core/directives/intersection-observer-directive';
	import { OverlayTypes } from '@core/classes/SharedEnums';
	import { PageViewport } from 'pdfjs-dist/legacy/build/pdf.mjs';
	import PDFInitials from '@core/components/PDFInitials.vue';
	import PDFPage from '@core/components/PDFPage.vue';
	import PDFPageFieldsOverlay from './PDFPageFieldsOverlay.vue';
	import PDFSignature from '@core/components/PDFSignature.vue';

	const EventBus = inject<Emitter<any>>('EventBus', null);

	//#region PROPS
	const { src, customDocumentInitParams, overlays, headers, signer, fields, onClickField, showFields, showPdfMeta, pageLimit } = defineProps<IPDFViewerProps>();
	//#endregion

	//#region EMITS
	const emit = defineEmits<{
		(e: 'pdfLoaded', pdf: PDFDocumentProxy | any): void;
		(e: 'rendered', pageData: { page: PDFPageData; index: number }): void;
		(e: 'error'): void;
	}>();

	const triggerRendered = (page: PDFPageData, index: number) => {
		emit('rendered', { page, index });
	};

	const triggerError = () => {
		emit('error');
	};
	//#endregion

	//#region DIRECTIVES
	const vSeen = IntersectionObserverDirective;
	//#endregion

	//#region DATA
	const pdf = ref<PDFDocumentProxy | null>(null);
	const pages = ref<PDFPageData[]>([]);
	const isBusy = ref<boolean>(false);
	const pagesRendered = ref<number>(0);

	const pdfRef = ref<HTMLElement | null>(null);
	//#endregion

	//#region COMPUTED

	const totalPages = computed(() => {
		return pdf?.value?.numPages ?? 0;
	});

	const maxPagesToRender = computed(() => {
		return Math.min(totalPages.value, pageLimit ?? Number.MAX_VALUE);
	});

	const pageViewport = computed(() => {
		if (pages.value.length < 1) return null;
		return pages.value[0].getViewport({ scale: 1 });
	});

	const pageSizeInches = computed(() => {
		if (!pageViewport.value) return null;

		// Viewport is in points, which is approx. 1/72nd of an inch
		return {
			width: pageViewport.value.width / 72,
			height: pageViewport.value.height / 72,
		};
	});

	//#endregion

	//#region LIFE CYCLE HOOKS
	onMounted(() => {
		window.addEventListener('resize', handleResize);
		showPDF();
	});

	onBeforeUnmount(() => {
		window.removeEventListener('resize', handleResize);
	});

	//#endregion

	watch(
		() => src,
		async () => {
			await showPDF();
		},
	);

	function handleResize() {
		//this.offsetwidth = this.$refs.pdf.offsetWidth
	}

	const showSignature = (overlay: IDocumentOverlay) => {
		//TODO make sure FI manager signature is showing
		return (
			overlay.overlayType == OverlayTypes.Signature &&
			(overlay.signatureMapping?.signerId == signer || overlay.signature.personType == ENUMS.PERSON_TYPES.FIMANAGER)
		);
	};

	const showInitials = (overlay: IDocumentOverlay) => {
		//TODO make sure FI manager initials are showing
		return (
			overlay.overlayType == OverlayTypes.Initials && (overlay.signatureMapping?.signerId == signer || overlay.initials.personType == ENUMS.PERSON_TYPES.FIMANAGER)
		);
	};

	async function showPDF() {
		isBusy.value = true;
		pagesRendered.value = 0;

		//LOADS PDFJS & WORKER
		// pdfjs.disableWorker = true;
		// pdfjs.disableStream = true;
		pdfjs.GlobalWorkerOptions.workerSrc = '/dist/js/pdf.worker.min.mjs';

		//CHECK IF WE ARE GETTING A URL TO A PDF FILE OR A 64-BASE String of PDF
		let dataSrc = null;
		if (customDocumentInitParams) {
			dataSrc = customDocumentInitParams;
		} else if (src?.includes('.pdf')) {
			dataSrc = {
				url: src,
				httpHeaders: headers,
				withCredentials: headers ? true : false,
				verbosity: 0,
			};
		} else {
			const arr = convertDataURIToBinary(src); // converts 64-base string to binary array
			dataSrc = {
				data: arr,
				verbosity: 0,
			};
		}

		// LOADS DOCUMENTS
		pdf.value = markRaw(await pdfjs.getDocument(dataSrc).promise);

		// eslint-disable-next-line no-console
		console.log('PDF LOADED', pdf);
		emit('pdfLoaded', pdf.value);
		pages.value = [];
	}

	function convertDataURIToBinary(dataURI: string): Uint8Array {
		var raw = window.atob(dataURI);
		var rawLength = raw.length;
		var array = new Uint8Array(new ArrayBuffer(rawLength));
		for (var i = 0; i < rawLength; i++) {
			array[i] = raw.charCodeAt(i);
		}
		return array;
	}

	function rendered(pageData: { page: PDFPageData; index: number }) {
		pagesRendered.value++;

		if (pagesRendered.value >= pages.value.length) {
			isBusy.value = false;
			emit('rendered', pageData); //ALL PAGES FINISHED LOADING
		}
	}

	function errorRendering() {
		emit('error');
	}

	function canDisplay(overlayMetadata: any): boolean {
		return (
			overlayMetadata?.overlay.isSigned ||
			overlayMetadata?.signatureMapping.signerId === signer ||
			overlayMetadata?.overlay.personType == ENUMS.PERSON_TYPES.FIMANAGER
		);
	}

	async function showNextPage() {
		if (pagesRendered.value >= maxPagesToRender.value) return;

		// PREPARES TO PROCESS PAGE
		const page: PDFPageData = await pdf.value.getPage(pagesRendered.value + 1);

		// SETS UP PAGE OVERLAYS (ONLY SIGNATURE OVERLAYS)
		if (overlays) {
			const scale = pdfRef.value.offsetWidth / page.getViewport({ scale: 1 }).width;
			page.scale = scale;

			page.random = getDateStampString() + randomIntFromInterval(1, 10000); // each page needs a random id
			page.overlays = overlays.filter(o => (o.signature && o.signature.pageNumber == page.pageNumber) || (o.initials && o.initials.pageNumber == page.pageNumber));

			page.isVisible = true;
			console.log('PAGE OVERLAYS', page.overlays);
		}

		if (fields) {
			const pageViewport = page.getViewport({ scale: 1 });
			page.fields = [];

			fields.fieldList.forEach(field => {
				if (field.page == page.pageNumber) {
					const fieldBox = previewFieldToFieldBox(field, pageViewport);
					page.fields.push(fieldBox);
				}
				field.otherPositions.forEach(oP => {
					if (oP.page == page.pageNumber) {
						const fieldBox = previewFieldToFieldBox(oP, pageViewport);
						page.fields.push(fieldBox);
					}
				});
			});
		}

		pages.value.push(markRaw(page));
		EventBus.emit('showNewPDFPage');
	}

	function previewFieldToFieldBox(field: PaperworkDocumentPreviewField, pageViewport: PageViewport): PDFFieldBox {
		const fieldsScale = pdfRef.value.offsetWidth / pageViewport.width;

		return {
			name: field.name,
			left: field.llx * fieldsScale,
			top: (pageViewport.height - field.ury) * fieldsScale,
			width: (field.urx - field.llx) * fieldsScale,
			height: (field.ury - field.lly) * fieldsScale,
			pageSize: pdfRef.value.offsetWidth,
			possibleValues: field.possibleValues,
			fullyQualifiedProperty: fields.mappedDataFields.find(mDF => mDF.fieldName?.split('~')[0] == field.name?.split('~')[0])?.fullyQualifiedProperty,
		};
	}

	function randomIntFromInterval(min: number, max: number): number {
		// min and max included
		return Math.floor(Math.random() * (max - min + 1) + min);
	}

	function getDateStampString(): string {
		return new Date()
			.toISOString()
			.replace(/[^0-9]/g, '')
			.slice(0, -3);
	}
</script>

<style>
	.pdf-container {
		z-index: 0;
	}

	.pxheight100 {
		height: 100px;
		background-color: #fff;
	}

	.pdf-preview-page-fields {
		position: relative;
	}

	.pdf-container .pdf-meta-list {
		display: flex;
		justify-content: flex-end;
		position: sticky;
		top: 0;
		padding: 10px;
		z-index: 999;
		pointer-events: none;
	}

	.pdf-container .pdf-meta-list > table {
		border: 2px solid var(--main-color);
		border-radius: var(--default-border-radius);
		background-color: var(--main-color-tinted);
		backdrop-filter: blur(3px);
		pointer-events: all;
	}

	.pdf-container .pdf-meta-list > table th {
		text-align: left;
	}
</style>
